import { Api } from "../../Api";
import { DependencyContainer } from "../../DependencyContainer";
import { Guid } from "../../Guid";
import { Helpers } from "../../Helpers";
import { DeviceResponseType, DeviceTaskType } from "../Devices/IDevice";
import { BatteryState, BinarySensorState, BlindPostionState, BlindRemoteButtonState, ChannelOnOffState, CurrentWindThresholdState, DoorBellState, EnergyMeasurementParameter, FacadeRemoteButtonState, FloodSensorState, GatePositionState, HumiditySensorState, LightBrightnessState, LightColorState, LightRGBWState, LightWarmthState, MeasuredBrightnessState, MeasuredEnergyState, MeasuredTemperatureState, MovementSensorState, PressureSensorState, ReedState, RemoteButtonState, WindSpeedState, WindThresholdStateEnum } from "../Devices/IDeviceState";
import { DeviceControlFeature, IDeviceTask } from "../Devices/IDeviceTask";
import { LightRGBW, SetBlindMicroventilation, SetBlindOpenCloseTime, SetBlindPosition, SetBlindPositionSimple, SetFacadePosition, SetGatePositionPrecise, SetLightBrightness, SetLightBrightnessDynamicly, SetLightColor, SetLightTemperature, SetTemperature, TurnOnWithTime } from "../Devices/Tasks/Tasks";
import { ILoggerService } from "../Logging/ILoggerService";
import { LoggerService } from "../Logging/LoggerService";
import { IUpdatesProvider } from "../Updates/IUpdatesProvider";
import { IUpdatesService } from "../Updates/IUpdatesService";
import { UpdatesProvider } from "../Updates/UpdatesProvider";
import { UpdatesService } from "../Updates/UpdatesService";
import { IScenesService } from "./IScenesService";
import { AstronomicalClockWithOffsetArgument, ConditionInfoArgument, DayOfWeekArgument, DeviceStateArgument, DeviceStateComparisonMethod, DeviceStateComparisonParams, SimpleTimeSpanArgument, TimeArgument, TimeSpanParam } from "./LeftArgumentTypes";
import { DelayTask, LockExecutionTask, LockType, NotifyTask, NotifyType } from "./SceneTaskTypes";
import { ConditionsTypes, DeviceStateType, HandledType, IRawSequence, ISequence, ISequenceInfo, ISequenceTasks, SceneTaskTypeNum, SupportedTaskTypes, UnsupportedScenesDeviceState } from "./Scenes";
import { ScenesService } from "./ScenesService";

export class SequenceBuilder {
    protected _result: SequenceRequestData | null = null;
    protected _tmp: SequenceRequestData = new SequenceRequestData();
    protected _tmpTask: ControllerTaskRequestData | null = null;

    private _service: IScenesService;
    private _prevSeqGuid = "";
    private _currentSeqGuid = "";

    private _editingExistingSequence = false;
    private _rawExistingSequenceTasks: TasksRequestData | null = null;

    //Saved in case when we remove first argument by method RemoveArgument
    private _sequenceName = "";
    private _sequenceIcon = "";

    constructor(sequenceName?: string, sequenceIcon: string = "Cube") {
        this._service = Api.Get<IScenesService>(ScenesService.ServiceName);

        if (sequenceName) {
            this._sequenceName = sequenceName;
            this._sequenceIcon = sequenceIcon;
            this._tmp.Guid = Guid.NewGuid();
        }
    }

    /**
     * Sets current (new or in edit) sequence name 
     * @param name name
     */
    public SetSequenceName(name: string): void {
        const findLastArgumentAndEditNameInNotifyTask = (sequence: SequenceRequestData): void => {
            if (sequence.RightArgumentType == ArgumentTypeNum.Null) {
                sequence.Tasks.ControllerTasks.where(t=> t.TaskType == SceneTaskTypeNum.NotifyTask).toArray().forEach(t => {
                    (t as NotifyTaskRequestData).Title = name;
                });
            }
            else
                return findLastArgumentAndEditNameInNotifyTask(sequence.RightArgument as SequenceRequestData);
        }

        this._sequenceName = name;

        if (this._editingExistingSequence && this._result != null) {
            this._result.Name = name;

            //Find notify task and update topic (name)
            findLastArgumentAndEditNameInNotifyTask(this._result);
        }
    }

    /**
     * Sets current (new or in edit) sequence icon 
     * @param icon icon
     */
    public SetSequenceIcon(icon: string): void {
        this._sequenceIcon = icon;

        if (this._editingExistingSequence && this._result != null) {
            this._result.Icon = icon;
        }
    }

    /**
     * Gets next sequence from sequence chain
     */
    public GetNext(): ISequence | null {
        if (this._result == null)
            return null;

        if (this._currentSeqGuid == "") {
            this._currentSeqGuid = this._result.Guid;
            return ((this._service as any).MapSequence(JSON.parse(JSON.stringify(this._result)), true) as ISequence);
        }
        else {
            const result = this.FindArgumentWithGuid(this._result, this._currentSeqGuid);

            if (result == null) {
                return null;
            }
            else {
                if (result.RightArgumentType == ArgumentTypeNum.Condition) {
                    this._prevSeqGuid = this._currentSeqGuid;
                    this._currentSeqGuid = (result.RightArgument as SequenceRequestData).Guid;

                    return ((this._service as any).MapSequence(JSON.parse(JSON.stringify(result.RightArgument)), true) as ISequence);
                }
                else return null;
            }
        }
    }

    /**
     * Method allows to clone builder with all sqeuence atributes and tasks.
     * Function re-generate all guids in chain and reset navigation to first element.
     * @returns cloned builder (new object)
     */
    public CloneCurrentBuilder(): SequenceBuilder {
        const regenerateArgumentsGuids = (builder: SequenceBuilder): void => {
            const regenerateGuids = (sequence: SequenceRequestData): void => {
                sequence.Guid = Guid.NewGuid();

                if (sequence.RightArgumentType == ArgumentTypeNum.Condition) {
                    regenerateGuids(sequence.RightArgument as SequenceRequestData)
                }
            }

            if (builder._result != null)
                regenerateGuids(builder._result);

            if (this._tmp != null)
                this._tmp.Guid = Guid.NewGuid();
        }

        var clonedBuilder = new SequenceBuilder();

        clonedBuilder._currentSeqGuid = "";
        clonedBuilder._prevSeqGuid = "";
        clonedBuilder._editingExistingSequence = structuredClone(this._editingExistingSequence);
        clonedBuilder._rawExistingSequenceTasks = structuredClone(this._rawExistingSequenceTasks);
        clonedBuilder._result = structuredClone(this._result);
        clonedBuilder._sequenceIcon = structuredClone(this._sequenceIcon);
        clonedBuilder._sequenceName = structuredClone(this._sequenceName);;
        clonedBuilder._tmp = structuredClone(this._tmp);

        regenerateArgumentsGuids(clonedBuilder);

        return clonedBuilder;
    }

    /**
     * Gets previous sequence from sequence chain
     */
    public GetPrevious(): ISequence | null {
        let prevGuid: string = "";

        const findArgumentWithGuidAndReturnPrevGuid = (sequence: SequenceRequestData, sequenceGuid: string): { sequence: SequenceRequestData, prevGuid: string } | null => {
            if (sequence.Guid == sequenceGuid) {
                return { sequence: sequence, prevGuid: prevGuid };
            }
            else if (sequence.RightArgumentType == ArgumentTypeNum.Condition) {
                prevGuid = sequence.Guid;
                return findArgumentWithGuidAndReturnPrevGuid(sequence.RightArgument as SequenceRequestData, sequenceGuid);
            }
            else return null;
        }

        if (this._result == null)
            return null;

        if (this._currentSeqGuid == "" || this._prevSeqGuid == "")
            return null;

        const result = findArgumentWithGuidAndReturnPrevGuid(this._result, this._prevSeqGuid);

        if (result == null) {
            return null;
        }
        else {
            this._currentSeqGuid = result.sequence.Guid;
            this._prevSeqGuid = result.prevGuid;

            return ((this._service as any).MapSequence(JSON.parse(JSON.stringify(result.sequence)), true) as ISequence);
        }
    }

    /**
     * Gets sequence by guid
     * @param sequenceGuid sequence guid
     */
    public GetByGuid(sequenceGuid: string): ISequence | null {
        if (this._result == null)
            return null;

        const arg = this.FindArgumentWithGuid(this._result, sequenceGuid);

        if (arg == null)
            return null;

        return (this._service as any).MapSequence(arg);
    }

    public EditCurrentSequence(sequence: IRawSequence): EditSequence {
        const findLastArgumentAndGetRawTasks = (sequence: SequenceRequestData): void => {
            if (sequence.RightArgumentType == ArgumentTypeNum.Null) {
                this._rawExistingSequenceTasks = JSON.parse(JSON.stringify(sequence.Tasks));
            }
            else
                return findLastArgumentAndGetRawTasks(sequence.RightArgument as SequenceRequestData);
        }

        this._editingExistingSequence = true;
        findLastArgumentAndGetRawTasks(sequence._rawRequestData);

        if (Object.hasOwn(sequence._rawRequestData, "Condition")) {
            this._result = (sequence._rawRequestData as any).Condition
        }
        else {
            this._result = sequence._rawRequestData;
        }

        //Getting raw tasks from last argument


        return {
            GetNext: () => this.GetNext(),
            GetPrevious: () => this.GetPrevious(),
            GetByGuid: (sequenceGuid: string) => this.GetByGuid(sequenceGuid),
            GetAllowedArguments: () => this.GetAllowedArguments(),
            RemoveSequence: (sequenceGuid: string) => this.RemoveSequenceByGuid(sequenceGuid),
            ArgumentAsAstronomicalClockWithOffset: (arg: AstronomicalClockWithOffsetArgument, atMeetCondition: boolean, comparasion?: ConditionsTypes) => this.ArgumentAsAstronomicalClockWithOffset(arg, atMeetCondition, comparasion),
            ArgumentAsDaysOfWeek: (arg: DayOfWeekArgument[], atMeetCondition: boolean, comparison?: ConditionsTypes) => this.ArgumentAsDaysOfWeek(arg, atMeetCondition, comparison),
            ArgumentAsDeviceState: (arg: DeviceStateArgument, atMeetCondition: boolean, atMeetConditionTimeout: number, comparison = ConditionsTypes.Equal) => this.ArgumentAsDeviceState(arg, atMeetCondition, atMeetConditionTimeout, comparison),
            ArgumentAsTime: (arg: TimeArgument, atMeetCondition: boolean, comparison = ConditionsTypes.Equal) => this.ArgumentAsTime(arg, atMeetCondition, comparison),
            ArgumentAsTimeSpan: (arg: SimpleTimeSpanArgument, comparison?: ConditionsTypes) => this.ArgumentAsTimeSpan(arg, comparison),
            ArgumentAsSceneExecuted: (arg: ISequenceInfo) => this.ArgumentAsSceneExecuted(arg),
            RemoveTask: (taskGuid: string) => this.RemoveTask(taskGuid),
        }
    }

    /**
     * Function creates argument of type AstronomicalClockWithOffse in the argument chain 
     * @param arg 
     * @returns function that adds argument to end of chain
     */
    public ArgumentAsAstronomicalClockWithOffset(arg: AstronomicalClockWithOffsetArgument, atMeetCondition: boolean, comparasion?: ConditionsTypes): AddOrEditArgument {
        this._tmp.Guid = Guid.NewGuid();
        this._tmp.HandledType = HandledType.AstronomicalClockWithOffset;
        this._tmp.LeftArgumentType = ArgumentTypeNum.ArgumentAsAstronomicalClockWithOffset;
        this._tmp.AtMeetCondition = atMeetCondition;

        if (arg.Offset < -3600 || arg.Offset > 3600)
            throw new BadParametersScenesBuilderException("Offset must be in range between -3600 and 3600 seconds.")

        if (atMeetCondition)
            this._tmp.ConditionType = ConditionsTypes.Equal;
        else if (!atMeetCondition && comparasion != null)
            this._tmp.ConditionType = comparasion;
        else
            throw new BadParametersScenesBuilderException("Comparasion type must be provided if atMeetCondition is false!")

        this._tmp.LeftArgument.Argument = arg;
        this._tmp.LeftArgument.HandledType = ArgumentTypeNum.ArgumentAsAstronomicalClockWithOffset;

        return {
            AddArgument: () => this.AddSequence(),
            EditArgument: (sequenceGuid: string) => this.EditSequence(sequenceGuid)
        };
    }

    public ArgumentAsActionWithEmptyCondition(): AddOrEditArgument {
        this._tmp.Guid = Guid.NewGuid();
        this._tmp.HandledType = HandledType.Unknown;
        this._tmp.LeftArgumentType = ArgumentTypeNum.Null;
        this._tmp.ConditionType = ConditionsTypes.Equal;

        this._tmp.LeftArgument.Argument = null;
        this._tmp.LeftArgument.HandledType = ArgumentTypeNum.Null;
        this._tmp.Name = this._sequenceName;
        this._tmp.Icon = this._sequenceIcon;
        this._tmp.AtMeetCondition = false;

        return {
            AddArgument: () => this.AddSequence(),
            EditArgument: (sequenceGuid: string) => this.EditSequence(sequenceGuid)
        };
    }

    /**
     * Function creates argument of type DayOfWeekArgument in the argument chain 
     * @param arg 
     * @param atMeetCondition 
     * @param comparison 
     * @returns function that adds argument to end of chain
     */
    public ArgumentAsDaysOfWeek(arg: DayOfWeekArgument[], atMeetCondition: boolean, comparison = ConditionsTypes.Equal): AddOrEditArgument {
        this._tmp.Guid = Guid.NewGuid();
        this._tmp.HandledType = HandledType.DaysOfWeek;
        this._tmp.LeftArgumentType = ArgumentTypeNum.ArgumentAsDaysOfWeek;
        this._tmp.ConditionType = comparison;

        this._tmp.LeftArgument.Argument = arg;
        this._tmp.LeftArgument.HandledType = ArgumentTypeNum.ArgumentAsDaysOfWeek;

        this._tmp.AtMeetCondition = atMeetCondition;

        return {
            AddArgument: () => this.AddSequence(),
            EditArgument: (sequenceGuid: string) => this.EditSequence(sequenceGuid)
        };
    }

    /**
     * Function creates argument of type SimpleTimeSpanArgument in the argument chain 
     * @param arg 
     * @param comparison 
     * @returns function that adds argument to end of chain
     */
    public ArgumentAsTimeSpan(arg: SimpleTimeSpanArgument, comparison = ConditionsTypes.Equal): AddOrEditArgument {
        this._tmp.Guid = Guid.NewGuid();
        this._tmp.HandledType = HandledType.Timer;
        this._tmp.LeftArgumentType = ArgumentTypeNum.ArgumentAsTimeSpan;
        this._tmp.ConditionType = comparison;
        this._tmp.AtMeetCondition = true;

        this._tmp.LeftArgument.Argument = arg;
        this._tmp.LeftArgument.HandledType = ArgumentTypeNum.ArgumentAsTimeSpan;

        return {
            AddArgument: () => this.AddSequence(),
            EditArgument: (sequenceGuid: string) => this.EditSequence(sequenceGuid)
        };
    }

    /**
     * Function creates argument of type ArgumentAsTime in the argument chain 
     * @param arg 
     * @param atMeetCondition 
     * @param comparison 
     * @returns function that adds argument to end of chain
     */
    public ArgumentAsTime(arg: TimeArgument, atMeetCondition: boolean, comparison = ConditionsTypes.Equal): AddOrEditArgument {
        this._tmp.Guid = Guid.NewGuid();
        this._tmp.HandledType = HandledType.Time;
        this._tmp.LeftArgumentType = ArgumentTypeNum.ArgumentAsTime;
        this._tmp.ConditionType = comparison;
        this._tmp.AtMeetCondition = atMeetCondition;

        this._tmp.LeftArgument.Argument = arg;
        this._tmp.LeftArgument.HandledType = ArgumentTypeNum.ArgumentAsTime;

        return {
            AddArgument: () => this.AddSequence(),
            EditArgument: (sequenceGuid: string) => this.EditSequence(sequenceGuid)
        };
    }

    /**
     * Function creates argument of type DeviceStateArgument in the argument chain 
     * @param arg 
     * @param atMeetCondition 
     * @param atMeetConditionTimeout 
     * @param comparison 
     * @returns function that adds argument to end of chain
     */
    public ArgumentAsDeviceState(arg: DeviceStateArgument, atMeetCondition: boolean, atMeetConditionTimeout: number, comparison = ConditionsTypes.Equal): AddOrEditArgument {
        this._tmp.Guid = Guid.NewGuid();
        this._tmp.HandledType = HandledType.DeviceState;
        this._tmp.LeftArgumentType = ArgumentTypeNum.ArgumentAsDeviceState;
        this._tmp.ConditionType = comparison;
        this._tmp.AtMeetCondition = atMeetCondition;
        this._tmp.ConditionTimeout = atMeetConditionTimeout;

        this._tmp.LeftArgument.Argument = this.GenerateDeviceStateArgument(arg);
        this._tmp.LeftArgument.HandledType = ArgumentTypeNum.ArgumentAsDeviceState;

        return {
            AddArgument: () => this.AddSequence(),
            EditArgument: (sequenceGuid: string) => this.EditSequence(sequenceGuid)
        };
    }

    /**
     * Function creates argument of type ArgumentAsSceneExecuted - this argument waits until given scene will be executed, then returns 'true' and allow to execute check rest of arguments in chain.
     * @param arg 
     */
    public ArgumentAsSceneExecuted(arg: ISequenceInfo): AddOrEditArgument {
        this._tmp.Guid = Guid.NewGuid();
        this._tmp.HandledType = HandledType.SceneExecuted;
        this._tmp.LeftArgumentType = ArgumentTypeNum.ArgumentAsConditionInfo;
        this._tmp.ConditionType = ConditionsTypes.Equal;
        this._tmp.AtMeetCondition = true;

        const argumentData = new ConditionInfoArgument();
        argumentData.DeviceGuid = arg.DeviceGuid;

        this._tmp.LeftArgument.Argument = argumentData;
        this._tmp.LeftArgument.HandledType = ArgumentTypeNum.ArgumentAsConditionInfo;

        return {
            AddArgument: () => this.AddSequence(),
            EditArgument: (sequenceGuid: string) => this.EditSequence(sequenceGuid)
        };
    }

    protected GenerateDeviceStateArgument(arg: DeviceStateArgument): LeftArgumentDeviceStateRequestData {
        const result = new LeftArgumentDeviceStateRequestData();
        result.Channel = arg.GetCheckDeviceState().Channel;
        result.ComparisonMethod = arg.ComparisonMethod;

        let rawParams = {}
        let paramsIterable = arg.GetComparisonParams().entries();
        for (const [param, paramVal] of paramsIterable) {
            rawParams = { ...rawParams, [DeviceStateComparisonParams[param]]: paramVal.Value }
        }

        result.ComparisonParams = rawParams;
        result.DeviceGuid = arg.DeviceGuid;
        result.DeviceStateType = this.MapResponseTypeToDeviceStateType(arg.Type);

        switch (arg.Type) {
            case DeviceResponseType.BatteryState: {
                result.CheckedDeviceState.State = arg.GetCheckDeviceState<BatteryState>().State;
                result.CheckedDeviceState.Percentage = arg.GetCheckDeviceState<BatteryState>().Percentage;
                break;
            }

            case DeviceResponseType.BinarySensorState: {
                result.CheckedDeviceState.State = arg.GetCheckDeviceState<BinarySensorState>().State;
                break;
            }

            case DeviceResponseType.BlindPosition: {
                result.CheckedDeviceState.Position = arg.GetCheckDeviceState<BlindPostionState>().Position;
                break;
            }

            case DeviceResponseType.LightBrightness: {
                result.CheckedDeviceState.Brightness = arg.GetCheckDeviceState<LightBrightnessState>().Brightness;
                break;
            }

            case DeviceResponseType.MeasuredBrightness: {
                result.CheckedDeviceState.Brightness = arg.GetCheckDeviceState<MeasuredBrightnessState>().Brightness;
                break;
            }

            case DeviceResponseType.RemoteButtonState: {
                result.CheckedDeviceState.State = arg.GetCheckDeviceState<RemoteButtonState>().State;
                break;
            }

            case DeviceResponseType.ChannelOnOffState: {
                result.CheckedDeviceState.State = arg.GetCheckDeviceState<ChannelOnOffState>().State;
                break;
            }

            case DeviceResponseType.DoorBellState: {
                result.CheckedDeviceState.State = arg.GetCheckDeviceState<DoorBellState>().State;
                break;
            }

            case DeviceResponseType.MeasuredEnergy: {
                let rawParams = {}
                let paramsIterable = arg.GetCheckDeviceState<MeasuredEnergyState>().MeasurementParameters.entries();
                for (const [param, paramVal] of paramsIterable) {
                    rawParams = { ...rawParams, [EnergyMeasurementParameter[param]]: paramVal }
                }

                result.CheckedDeviceState.MeasurementParameters = rawParams;
                break;
            }

            case DeviceResponseType.FloodSensorState: {
                result.CheckedDeviceState.State = arg.GetCheckDeviceState<FloodSensorState>().State;
                break;
            }

            case DeviceResponseType.GatePosition: {
                result.CheckedDeviceState.Position = arg.GetCheckDeviceState<GatePositionState>().Position;
                result.CheckedDeviceState.PositionType = arg.GetCheckDeviceState<GatePositionState>().PositionType;
                break;
            }

            case DeviceResponseType.LightColor: {
                const st = arg.GetCheckDeviceState<LightColorState>();
                result.CheckedDeviceState.R = st.R;
                result.CheckedDeviceState.G = st.G;
                result.CheckedDeviceState.B = st.B;
                break;
            }

            case DeviceResponseType.LightWarmth: {
                result.CheckedDeviceState.Temperature = arg.GetCheckDeviceState<LightWarmthState>().Temperature;
                break;
            }

            case DeviceResponseType.ReedState: {
                result.CheckedDeviceState.State = arg.GetCheckDeviceState<ReedState>().State;
                break;
            }

            case DeviceResponseType.SmokeSensorState:
                throw new UnsupportedScenesDeviceState("Unsupported device state!");


            case DeviceResponseType.MeasuredTemperature: {
                result.CheckedDeviceState.Temperature = arg.GetCheckDeviceState<MeasuredTemperatureState>().Temperature;
                break;
            }

            case DeviceResponseType.HumiditySensorState: {
                result.CheckedDeviceState.Humidity = arg.GetCheckDeviceState<HumiditySensorState>().Humidity;
                break;
            }

            case DeviceResponseType.BlindRemoteButtonState: {
                result.CheckedDeviceState.State = arg.GetCheckDeviceState<BlindRemoteButtonState>().State;
                break;
            }

            case DeviceResponseType.LightRGBWState: {
                let st = arg.GetCheckDeviceState<LightRGBWState>();
                result.CheckedDeviceState.R = st.R;
                result.CheckedDeviceState.G = st.G;
                result.CheckedDeviceState.B = st.B;
                result.CheckedDeviceState.W = st.W;
                break;
            }

            case DeviceResponseType.FacadeRemoteButtonState: {
                result.CheckedDeviceState.State = arg.GetCheckDeviceState<FacadeRemoteButtonState>().State;
                break;
            }

            case DeviceResponseType.MovementSensorState: {
                let st = arg.GetCheckDeviceState<MovementSensorState>();
                result.CheckedDeviceState.Movement = st.Movement;
                result.CheckedDeviceState.Intensity = st.Intensity;
                break;
            }

            case DeviceResponseType.PressureSensorState: {
                result.CheckedDeviceState.Pressure = arg.GetCheckDeviceState<PressureSensorState>().Pressure;
                result.CheckedDeviceState.PressureType = arg.GetCheckDeviceState<PressureSensorState>().PressureType;
                break;
            }

            case DeviceResponseType.WindSpeedState: {
                result.CheckedDeviceState.Value = arg.GetCheckDeviceState<WindSpeedState>().Value;
                break;
            }

            case DeviceResponseType.CurrentWindThreshold: {
                result.CheckedDeviceState.WindThreshold = arg.GetCheckDeviceState<CurrentWindThresholdState>().WindThreshold;
                break;
            }

            case DeviceResponseType.Unknown:
                result.CheckedDeviceState = {};
                break;

            default:
                throw new UnsupportedScenesDeviceState("Unsupported device state!");
        }

        return result;
    }

    protected MapResponseTypeToDeviceStateType(responseType: DeviceResponseType): DeviceStateType {
        switch (responseType) {
            case DeviceResponseType.BatteryState:
                return DeviceStateType.BatteryState;
            case DeviceResponseType.BinarySensorState:
                return DeviceStateType.BinarySensor;
            case DeviceResponseType.BlindPosition:
                return DeviceStateType.BlindPosition;
            case DeviceResponseType.LightBrightness:
                return DeviceStateType.LightBrightness;
            case DeviceResponseType.RemoteButtonState:
                return DeviceStateType.ButtonState;
            case DeviceResponseType.ChannelOnOffState:
                return DeviceStateType.ChannelOnOff;
            case DeviceResponseType.DoorBellState:
                return DeviceStateType.DoorBell;
            case DeviceResponseType.MeasuredEnergy:
                return DeviceStateType.Energy;
            case DeviceResponseType.FloodSensorState:
                return DeviceStateType.FloodSensor;
            case DeviceResponseType.GatePosition:
                return DeviceStateType.GatePosition;
            case DeviceResponseType.MeasuredBrightness:
                return DeviceStateType.Brightness;
            case DeviceResponseType.LightColor:
                return DeviceStateType.LightColor;
            case DeviceResponseType.LightWarmth:
                return DeviceStateType.LightTemperature;
            case DeviceResponseType.ReedState:
                return DeviceStateType.ReedState;
            case DeviceResponseType.SmokeSensorState:
                return DeviceStateType.SmokeSensor;
            case DeviceResponseType.MeasuredTemperature:
                return DeviceStateType.Temperature;
            case DeviceResponseType.HumiditySensorState:
                return DeviceStateType.Humidity;
            case DeviceResponseType.BlindRemoteButtonState:
                return DeviceStateType.BlindsControlButton;
            case DeviceResponseType.FacadeRemoteButtonState:
                return DeviceStateType.FacadeControlButton;
            case DeviceResponseType.MovementSensorState:
                return DeviceStateType.Movement;
            case DeviceResponseType.PressureSensorState:
                return DeviceStateType.AirPressure;
            case DeviceResponseType.WindSpeedState:
                return DeviceStateType.WindSpeed;
            case DeviceResponseType.CurrentWindThreshold:
                return DeviceStateType.WindThreshold;

            default:
                return DeviceStateType.Unknown;
        }
    }

    /**
     * Function adds argument to the end of argument chain
     * @returns Functions that coud create another argument, function that coud commit argument chain, current argument data. 
     */
    protected AddSequence(): Commit {
        const findLastRightArgumentAndAddContition = (sequence: SequenceRequestData, newData: SequenceRequestData): void => {
            if (sequence.RightArgumentType == ArgumentTypeNum.Null) {
                sequence.RightArgumentType = ArgumentTypeNum.Condition;
                sequence.RightArgument = newData;

                if (this._editingExistingSequence) {
                    if (this._rawExistingSequenceTasks != null) {
                        newData.Tasks = JSON.parse(JSON.stringify(this._rawExistingSequenceTasks));
                        sequence.Tasks = new TasksRequestData();
                    }
                }
            }
            else
                return findLastRightArgumentAndAddContition(sequence.RightArgument as SequenceRequestData, newData);
        }

        //result null - root sequence
        if (this._result == null) {
            this._tmp.Name = this._sequenceName;
            this._tmp.Icon = this._sequenceIcon;
            this._result = this._tmp;
            this._result.AtMeetCondition = true;
        }
        else
            findLastRightArgumentAndAddContition(this._result, this._tmp);

        const sequneceData = ((this._service as any).MapSequence(JSON.parse(JSON.stringify(this._tmp))) as ISequence);

        this._tmp = new SequenceRequestData();

        return {
            GetNext: () => this.GetNext(),
            GetPrevious: () => this.GetPrevious(),
            GetByGuid: (sequenceGuid: string) => this.GetByGuid(sequenceGuid),
            GetAllowedArguments: () => this.GetAllowedArguments(),
            RemoveSequenceByGuid: (argumentGuid: string) => this.RemoveSequenceByGuid(argumentGuid),
            Commit: () => this.Commit(),
            ArgumentAsActionWithEmptyCondition: () => this.ArgumentAsActionWithEmptyCondition(),
            ArgumentAsAstronomicalClockWithOffset: (arg: AstronomicalClockWithOffsetArgument, atMeetCondition: boolean, comparasion?: ConditionsTypes) => this.ArgumentAsAstronomicalClockWithOffset(arg, atMeetCondition, comparasion),
            ArgumentAsDaysOfWeek: (arg: DayOfWeekArgument[], atMeetCondition: boolean, comparison?: ConditionsTypes) => this.ArgumentAsDaysOfWeek(arg, atMeetCondition, comparison),
            ArgumentAsDeviceState: (arg: DeviceStateArgument, atMeetCondition: boolean, atMeetConditionTimeout: number, comparison = ConditionsTypes.Equal) => this.ArgumentAsDeviceState(arg, atMeetCondition, atMeetConditionTimeout, comparison),
            ArgumentAsTime: (arg: TimeArgument, atMeetCondition: boolean, comparison = ConditionsTypes.Equal) => this.ArgumentAsTime(arg, atMeetCondition, comparison),
            ArgumentAsTimeSpan: (arg: SimpleTimeSpanArgument, comparison?: ConditionsTypes) => this.ArgumentAsTimeSpan(arg, comparison),
            ArgumentAsSceneExecuted: (arg: ISequenceInfo) => this.ArgumentAsSceneExecuted(arg),
            SequenceData: sequneceData
        }
    }

    protected EditSequence(sequenceGuid: string): Commit {
        const findLastArgumentWithGuidAndEditCondition = (sequence: SequenceRequestData, newData: SequenceRequestData): void => {
            if (sequence.Guid == sequenceGuid && sequence.ObjectType == ArgumentTypeNum.Condition) {
                //Remap properties (needs in this format to keep reference to this._result, we changing only sequence properties without deattach root) newData is reference to this._tmp
                sequence.AtMeetCondition = newData.AtMeetCondition;
                sequence.ConditionTimeout = newData.ConditionTimeout;
                sequence.ConditionType = newData.ConditionType;
                sequence.ExecuteOnce = newData.ExecuteOnce;
                sequence.HandledType = newData.HandledType;
                sequence.IsDisabled = newData.IsDisabled;
                sequence.LeftArgument = newData.LeftArgument;
                sequence.LeftArgumentType = newData.LeftArgumentType;
                sequence.ObjectType = newData.ObjectType;
                sequence.Tasks = newData.Tasks;

                //Keep settings that are not edited in this scope
                newData.Guid = sequence.Guid;
                newData.Icon = sequence.Icon;
                newData.Name = sequence.Name;
            }
            else if (sequence.RightArgumentType == ArgumentTypeNum.Condition)
                return findLastArgumentWithGuidAndEditCondition(sequence.RightArgument as SequenceRequestData, newData);
            else throw new SequenceNotFoundScenesBuilderException(`Cannot edit sequence with given GUID ${sequenceGuid} - sequence not foud in chain`);
        }

        if (this._result == null)
            throw new SequenceNotFoundScenesBuilderException(`Cannot edit sequence with given GUID ${sequenceGuid} - sequence not initialized (no root element)`);

        findLastArgumentWithGuidAndEditCondition(this._result, this._tmp);

        const sequneceData = ((this._service as any).MapSequence(JSON.parse(JSON.stringify(this._tmp))) as ISequence);

        this._tmp = new SequenceRequestData();
        console.log(`new tmp guid: ${this._tmp.Guid}`)

        if (!this._result.AtMeetCondition)
            this._result.AtMeetCondition = true;

        return {
            GetNext: () => this.GetNext(),
            GetPrevious: () => this.GetPrevious(),
            GetByGuid: (sequenceGuid: string) => this.GetByGuid(sequenceGuid),
            GetAllowedArguments: () => this.GetAllowedArguments(),
            RemoveSequenceByGuid: (sequenceGuid: string) => this.RemoveSequenceByGuid(sequenceGuid),
            Commit: () => this.Commit(),
            ArgumentAsActionWithEmptyCondition: () => this.ArgumentAsActionWithEmptyCondition(),
            ArgumentAsAstronomicalClockWithOffset: (arg: AstronomicalClockWithOffsetArgument, atMeetCondition: boolean, comparasion?: ConditionsTypes) => this.ArgumentAsAstronomicalClockWithOffset(arg, atMeetCondition, comparasion),
            ArgumentAsDaysOfWeek: (arg: DayOfWeekArgument[], atMeetCondition: boolean, comparison?: ConditionsTypes) => this.ArgumentAsDaysOfWeek(arg, atMeetCondition, comparison),
            ArgumentAsDeviceState: (arg: DeviceStateArgument, atMeetCondition: boolean, atMeetConditionTimeout: number, comparison = ConditionsTypes.Equal) => this.ArgumentAsDeviceState(arg, atMeetCondition, atMeetConditionTimeout, comparison),
            ArgumentAsTime: (arg: TimeArgument, atMeetCondition: boolean, comparison = ConditionsTypes.Equal) => this.ArgumentAsTime(arg, atMeetCondition, comparison),
            ArgumentAsTimeSpan: (arg: SimpleTimeSpanArgument, comparison?: ConditionsTypes) => this.ArgumentAsTimeSpan(arg, comparison),
            ArgumentAsSceneExecuted: (arg: ISequenceInfo) => this.ArgumentAsSceneExecuted(arg),
            SequenceData: sequneceData
        }
    }

    public RemoveSequenceByGuid(sequenceGuid: string): Commit {
        let refToHead: SequenceRequestData;

        const fixNavigation = (sequence: SequenceRequestData, sequenceGuid: string) => {
            //navigation never used, don't need fix
            if (this._currentSeqGuid == "" && this._prevSeqGuid == "")
                return;

            if (this._currentSeqGuid == sequenceGuid) {
                this._currentSeqGuid = sequence.RightArgumentType == ArgumentTypeNum.Condition ? (sequence.RightArgument as SequenceRequestData).Guid : "";
                this._prevSeqGuid = refToHead != null ? refToHead.Guid : "";
            }
            else if (this._prevSeqGuid == sequenceGuid) {
                this._prevSeqGuid = refToHead != null ? refToHead.Guid : "";
            }
        }

        const findRightArgumentAndRemoveArg = (sequence: SequenceRequestData, sequenceGuid: string): void => {
            //Argument to remove
            if (sequence.Guid == sequenceGuid) {

                //no ref to head - root argument
                if (refToHead == null)
                    this._result = null;
                else {
                    refToHead.RightArgument = sequence.RightArgument;
                    refToHead.RightArgumentType = sequence.RightArgumentType;
                }

                fixNavigation(sequence, sequenceGuid);
            }
            else {
                refToHead = sequence

                if (sequence.RightArgumentType == ArgumentTypeNum.Null)
                    throw new SequenceNotFoundScenesBuilderException(`Cannot remove sequence, sequence with given guid ${sequenceGuid} not found!`);

                return findRightArgumentAndRemoveArg(sequence.RightArgument as SequenceRequestData, sequenceGuid);
            }

        }
        if (this._result == null)
            throw new SequenceNotFoundScenesBuilderException("Cannot remove sequence, sequence not configured!")

        //Removing root argument
        if (this._result.Guid == sequenceGuid && this._result.RightArgumentType != ArgumentTypeNum.Null) {
            (this._result.RightArgument as SequenceRequestData).Name = this._result.Name;
            (this._result.RightArgument as SequenceRequestData).Icon = this._result.Icon;
            (this._result.RightArgument as SequenceRequestData).Guid = this._result.Guid;
            this._result = (this._result.RightArgument as SequenceRequestData);
            this._result.AtMeetCondition = true;

            fixNavigation(this._result, sequenceGuid);
        }
        else {
            //Removing nested arguments
            findRightArgumentAndRemoveArg(this._result, sequenceGuid);
        }

        return {
            GetNext: () => this.GetNext(),
            GetPrevious: () => this.GetPrevious(),
            GetByGuid: (sequenceGuid: string) => this.GetByGuid(sequenceGuid),
            GetAllowedArguments: () => this.GetAllowedArguments(),
            RemoveSequenceByGuid: (sequenceGuid: string) => this.RemoveSequenceByGuid(sequenceGuid),
            Commit: () => this.Commit(),
            ArgumentAsActionWithEmptyCondition: () => this.ArgumentAsActionWithEmptyCondition(),
            ArgumentAsAstronomicalClockWithOffset: (arg: AstronomicalClockWithOffsetArgument, atMeetCondition: boolean, comparasion?: ConditionsTypes) => this.ArgumentAsAstronomicalClockWithOffset(arg, atMeetCondition, comparasion),
            ArgumentAsDaysOfWeek: (arg: DayOfWeekArgument[], atMeetCondition: boolean, comparison?: ConditionsTypes) => this.ArgumentAsDaysOfWeek(arg, atMeetCondition, comparison),
            ArgumentAsDeviceState: (arg: DeviceStateArgument, atMeetCondition: boolean, atMeetConditionTimeout: number, comparison = ConditionsTypes.Equal) => this.ArgumentAsDeviceState(arg, atMeetCondition, atMeetConditionTimeout, comparison),
            ArgumentAsTime: (arg: TimeArgument, atMeetCondition: boolean, comparison = ConditionsTypes.Equal) => this.ArgumentAsTime(arg, atMeetCondition, comparison),
            ArgumentAsTimeSpan: (arg: SimpleTimeSpanArgument, comparison?: ConditionsTypes) => this.ArgumentAsTimeSpan(arg, comparison),
            ArgumentAsSceneExecuted: (arg: ISequenceInfo) => this.ArgumentAsSceneExecuted(arg),
            SequenceData: null
        }
    }

    /**
     * Functions commits modifications of arguments chain
     * @returns 
     */
    public Commit(): AddOrRemoveTasks {
        const findLastRightArgumentAndMoveTasks = (sequence: SequenceRequestData): void => {
            //Last arg
            if (sequence.RightArgumentType == ArgumentTypeNum.Null) {
                if (this._rawExistingSequenceTasks != null)
                    sequence.Tasks = this._rawExistingSequenceTasks;
            }
            else
                return findLastRightArgumentAndMoveTasks(sequence.RightArgument as SequenceRequestData);
        }

        if (this._result == null)
            throw new SequenceNotFoundScenesBuilderException("Sequence are not initialized! Not found root sequence.");

        //Move tasks to last argument - required while editing (removing or adding argument) 
        if (this._editingExistingSequence)
            findLastRightArgumentAndMoveTasks(this._result);

        return {
            GetSupportedTypesOfTasks: () => this.GetSupportedTaskTypesAsync(),
            DeviceTask: (task: IDeviceTask) => this.DeviceTask(task),
            DelayTask: (delay: DelayTask) => this.DelayTask(delay),
            NotifyTask: (notification: NotifyTask) => this.NotifyTask(notification),
            LockExecutionTask: (lockTask: LockExecutionTask) => this.LockExecutionTask(lockTask),
            RemoveTask: (taskGuid: string) => this.RemoveTask(taskGuid),
            Build: () => this.Build(),
            BuildToISequence: () => ((this._service as any).MapSequence(JSON.parse(JSON.stringify(this._result))) as ISequence),
            ValidateScene: () => this.ValidateScene(),
        }
    }

    protected DeviceTask(task: IDeviceTask): AddOrEditTask {
        this._tmpTask = this.ParseDeviceTask(task);

        if (this._result == null)
            throw new Error("No context!")

        return {
            AddTask: () => this.AddTask(),
            EditTask: (taskId: string) => this.EditTask(taskId),
            TaskId: Helpers.GenerateMd5(JSON.stringify(this._tmpTask)),
        }
    }

    private ParseDeviceTask(task: IDeviceTask): DeviceTaskRequestData {
        const tsk = new DeviceTaskRequestData();
        tsk.Channel = task.Channel;
        tsk.DeviceGuid = task.DeviceGuid;
        tsk.ControlFeature = this.MapTaskTypeToControlFeature(task.TaskType);
        tsk.Data = {};
        tsk.Data.Channel = task.Channel;

        if (task.TaskType == DeviceTaskType.SetBlindPosition || task instanceof SetBlindPosition) {
            tsk.Data.Position = (task as any).Position;
            tsk.Data.Action = (task as any).Action;
        }
        else if(task.TaskType == DeviceTaskType.SetBlindPositionSimple || task instanceof SetBlindPositionSimple) {
            tsk.Data.Action = (task as SetBlindPositionSimple).Action;
            tsk.Data.Position = 0;
        }
        else if (task.TaskType == DeviceTaskType.SetLightColor || task instanceof SetLightColor) {
            tsk.Data.R = (task as any).R;
            tsk.Data.G = (task as any).G;
            tsk.Data.B = (task as any).B;
        }
        else if (task.TaskType == DeviceTaskType.SetLightTemperature || task instanceof SetLightTemperature) {
            tsk.Data.Temperature = (task as any).Temperature;
        }
        else if (task.TaskType == DeviceTaskType.SetLightBrightness || task instanceof SetLightBrightness) {
            tsk.Data.Brightness = (task as any).Brightness;
        }
        else if (task.TaskType == DeviceTaskType.SetTemperature || task instanceof SetTemperature) {
            tsk.Data.Temperature = (task as any).Temperature;
        }
        else if (task.TaskType == DeviceTaskType.TurnOnWithTime || task instanceof TurnOnWithTime) {
            tsk.Data.SwitchOffDelaySeconds = (task as any).SwitchOffDelaySeconds;
        }
        else if (task.TaskType == DeviceTaskType.SetLightBrightnessDynamicly || task instanceof SetLightBrightnessDynamicly) {
            tsk.Data.Brightness = (task as any).Brightness;
        }
        else if (task.TaskType == DeviceTaskType.SetBlindOpenCloseTime || task instanceof SetBlindOpenCloseTime) {
            tsk.Data.CloseTime = (task as any).Data.CloseTime;
            tsk.Data.OpenTime = (task as any).Data.OpenTime;
        }
        else if (task.TaskType == DeviceTaskType.SetBlindMicroventilation || task instanceof SetBlindMicroventilation) {
            tsk.Data.Position = (task as any).Position;
        }
        else if (task.TaskType == DeviceTaskType.SetFacadePosition || task instanceof SetFacadePosition) {
            tsk.Data.FacadeAction = (task as any).FacadeAction;
            tsk.Data.Position = (task as any).Position;
            tsk.Data.Tilt = (task as any).Tilt;
        }
        else if (task.TaskType == DeviceTaskType.LightRGBW || task instanceof LightRGBW) {
            tsk.Data.R = (task as any).Data.R;
            tsk.Data.G = (task as any).Data.G;
            tsk.Data.B = (task as any).Data.B;
            tsk.Data.W = (task as any).Data.W;
            tsk.Data.Brightness = (task as any).Data.Brightness;
        }
        else if (task.TaskType == DeviceTaskType.PreciseGateControl || task instanceof SetGatePositionPrecise)  {
            tsk.Data.Position = (task as any).Position;
            tsk.Data.GateControlAction = (task as any).GateControlAction;
        }
        //else
        //    throw new Error(`Unsupported device task, task type: ${task.TaskType} task object: ${task}!`);

        return tsk;
    }

    protected DelayTask(delay: DelayTask): AddOrEditTask {
        const task = new DelayTaskRequestData();

        if(delay.Delay.Value == "00:00:00")
            throw new BadParametersScenesBuilderException("Delay must be other than 00:00:00!");

        task.Delay = delay.Delay.Value;

        this._tmpTask = task;
        if (this._result == null)
            throw new Error("No context!")

        return {
            AddTask: () => this.AddTask(),
            EditTask: (taskGuid: string) => this.EditTask(taskGuid),
            TaskId: Helpers.GenerateMd5(JSON.stringify(task)),
        }
    }

    protected NotifyTask(notification: NotifyTask): AddOrEditTask {
        if (notification.NotifyType == NotifyType.Email)
            throw new UnsupportedArgumentScenesBuilderException("NotifyType 'Email' is not supported!");

        if (notification.NotifyType == NotifyType.None)
            throw new Error("NotifyType mus be set!");

        if (notification.Message == "")
            throw new BadParametersScenesBuilderException("Message must be set!");

        if (notification.Message.length > 1000)
            throw new BadParametersScenesBuilderException("Message is too long! Max length is 1000 characters.");

        const task = new NotifyTaskRequestData();
        task.NotifyType = notification.NotifyType;
        task.Message = notification.Message;
        task.NotificationClients = notification.NotificationClients;
        task.Title = this._sequenceName;

        this._tmpTask = task;
        if (this._result == null)
            throw new Error("No context!")

        return {
            AddTask: () => this.AddTask(),
            EditTask: (taskGuid: string) => this.EditTask(taskGuid),
            TaskId: Helpers.GenerateMd5(JSON.stringify(task)),
        }
    }

    protected LockExecutionTask(lockTask: LockExecutionTask): AddOrEditTask {
        const task = new LockTaskTaskRequestData();
        task.LockType = lockTask.LockType;

        if (task.LockType == LockType.Timeout) {
            if (task.LockEndHour != "00:00:00")
                throw new UnsupportedArgumentScenesBuilderException("Parameter LockEndHour at lock type `Timeout` must be default!");
        }

        task.LockStartHour = lockTask.LockStartHour.Value;
        task.LockEndHour = lockTask.LockEndHour.Value;
        task.ResetLockScenesGuids = lockTask.ResetLockScenesGuids;

        this._tmpTask = task;

        if (this._result == null)
            throw new Error("No context!")

        return {
            AddTask: () => this.AddTask(),
            EditTask: (taskGuid: string) => this.EditTask(taskGuid),
            TaskId: Helpers.GenerateMd5(JSON.stringify(task)),
        }
    }

    private AddTask(): AddOrRemoveTasks {
        const findLastRightArgumentAndAddTask = (sequence: SequenceRequestData): void => {
            //Last arg
            if (sequence.RightArgumentType == ArgumentTypeNum.Null) {
                if (this._tmpTask == null)
                    throw Error("No task context!")

                if (sequence.Tasks.DevicesTasks === undefined || sequence.Tasks.DevicesTasks === null)
                    sequence.Tasks.DevicesTasks = [];

                if (this._tmpTask instanceof DeviceTaskRequestData)
                    sequence.Tasks.DevicesTasks.push(structuredClone(this._tmpTask));

                sequence.Tasks.ControllerTasks.push(structuredClone(this._tmpTask));
                this._tmpTask = null;

                DependencyContainer.Log?.Debug(`Task added to sequence: \n${JSON.stringify(this._tmpTask)} \ntasks on a list: \n${JSON.stringify(sequence.Tasks)}`);
            }
            else
                return findLastRightArgumentAndAddTask(sequence.RightArgument as SequenceRequestData);
        }

        if (this._result == null)
            throw new Error("No context!")

        findLastRightArgumentAndAddTask(this._result)

        return {
            GetSupportedTypesOfTasks: () => this.GetSupportedTaskTypesAsync(),
            DeviceTask: (task: IDeviceTask) => this.DeviceTask(task),
            DelayTask: (delay: DelayTask) => this.DelayTask(delay),
            NotifyTask: (notification: NotifyTask) => this.NotifyTask(notification),
            LockExecutionTask: (lockTask: LockExecutionTask) => this.LockExecutionTask(lockTask),
            RemoveTask: (taskId: string) => this.RemoveTask(taskId),
            Build: () => this.Build(),
            BuildToISequence: () => ((this._service as any).MapSequence(JSON.parse(JSON.stringify(this._result))) as ISequence),
            ValidateScene: () => this.ValidateScene(),
        }
    }

    protected EditTask(taskId: string): AddOrRemoveTasks {
        const findLastRightArgumentAndEditTask = (sequence: SequenceRequestData): void => {
            //Last arg
            if (sequence.RightArgumentType == ArgumentTypeNum.Null) {
                if (this._tmpTask == null)
                    throw Error("No task context!")

                if (sequence.Tasks.ControllerTasks == null || sequence.Tasks.ControllerTasks.length == 0)
                    throw new TaskNotFoundScenesBuilderException("Task not found, cannot edit!")

                if (this._tmpTask instanceof DeviceTaskRequestData && sequence.Tasks.DevicesTasks != null)
                    sequence.Tasks.DevicesTasks = sequence.Tasks.DevicesTasks.map(dTask => Helpers.GenerateMd5(JSON.stringify(dTask)) == taskId ? structuredClone(this._tmpTask! as DeviceTaskRequestData) : dTask);

                sequence.Tasks.ControllerTasks = sequence.Tasks.ControllerTasks.map(cTask => Helpers.GenerateMd5(JSON.stringify(cTask)) == taskId ? structuredClone(this._tmpTask! as DeviceTaskRequestData) : cTask);

                this._tmpTask = null;
                DependencyContainer.Log?.Debug(`Task edited in sequence: \n${JSON.stringify(this._tmpTask)} \ntasks on a list: \n${JSON.stringify(sequence.Tasks)}`);
            }
            else
                return findLastRightArgumentAndEditTask(sequence.RightArgument as SequenceRequestData);
        }

        if (this._result == null)
            throw new Error("No context!")

        findLastRightArgumentAndEditTask(this._result);

        return {
            GetSupportedTypesOfTasks: () => this.GetSupportedTaskTypesAsync(),
            DeviceTask: (task: IDeviceTask) => this.DeviceTask(task),
            DelayTask: (delay: DelayTask) => this.DelayTask(delay),
            NotifyTask: (notification: NotifyTask) => this.NotifyTask(notification),
            LockExecutionTask: (lockTask: LockExecutionTask) => this.LockExecutionTask(lockTask),
            RemoveTask: (taskGuid: string) => this.RemoveTask(taskGuid),
            Build: () => this.Build(),
            BuildToISequence: () => ((this._service as any).MapSequence(JSON.parse(JSON.stringify(this._result))) as ISequence),
            ValidateScene: () => this.ValidateScene(),
        }
    }

    protected RemoveTask(taskId: string) {
        const findLastRightArgumentAndRemoveTask = (sequence: SequenceRequestData, taskId: string): void => {
            //Last arg
            if (sequence.RightArgumentType == ArgumentTypeNum.Null) {
                const newTasks = sequence.Tasks.ControllerTasks.filter(t => Helpers.GenerateMd5(JSON.stringify(t)) != taskId);

                if (newTasks == null)
                    sequence.Tasks.ControllerTasks = []
                else
                    sequence.Tasks.ControllerTasks = newTasks;

                if (sequence.Tasks?.DevicesTasks?.any()) {
                    const newTasks = sequence.Tasks.DevicesTasks.filter(t => Helpers.GenerateMd5(JSON.stringify(t)) != taskId);

                    if (newTasks == null)
                        sequence.Tasks.DevicesTasks = []
                    else
                        sequence.Tasks.DevicesTasks = newTasks;
                }
            }
            else
                return findLastRightArgumentAndRemoveTask(sequence.RightArgument as SequenceRequestData, taskId);
        }
        if (this._result == null)
            throw Error("Cannot remove task, sequence not configured!")

        findLastRightArgumentAndRemoveTask(this._result, taskId);
    }

    protected MapTaskTypeToControlFeature(taskType: DeviceTaskType): DeviceControlFeature {
        switch (taskType) {
            case DeviceTaskType.Unknown:
                throw new BadParametersScenesBuilderException("Unsupported task type!");
            case DeviceTaskType.SetBlindPosition:
                return DeviceControlFeature.SetBlindPosition;
            case DeviceTaskType.SetBlindPositionSimple:
                return DeviceControlFeature.SetBlindPosition;
            case DeviceTaskType.SetBlindMicroventilation:
                return DeviceControlFeature.SetBlindMicroventilation;
            case DeviceTaskType.TurnOff:
                return DeviceControlFeature.TurnOff;
            case DeviceTaskType.TurnOn:
                return DeviceControlFeature.TurnOn;
            case DeviceTaskType.TurnOnWithTime:
                return DeviceControlFeature.TurnOnWithTimeout;
            case DeviceTaskType.TogleState:
                return DeviceControlFeature.ToggleState;
            case DeviceTaskType.SetLightBrightnessDynamicly:
                return DeviceControlFeature.SetLightBrightnessDynamicly;
            case DeviceTaskType.SetLightBrightness:
                return DeviceControlFeature.SetLightBrightness;
            case DeviceTaskType.SetLightColor:
                return DeviceControlFeature.SetLightColor;
            case DeviceTaskType.SetLightTemperature:
                return DeviceControlFeature.SetLightTemperature;
            case DeviceTaskType.PairDevice:
                throw new BadParametersScenesBuilderException("Unsupported task type!");
            case DeviceTaskType.UnpairDevice:
                throw new BadParametersScenesBuilderException("Unsupported task type!");
            case DeviceTaskType.IdentifyDevice:
                return DeviceControlFeature.IdentifyDevice;
            case DeviceTaskType.GetChannelsState:
                return DeviceControlFeature.GetState;
            case DeviceTaskType.GetPowerMeasurements:
                throw new BadParametersScenesBuilderException("Unsupported task type!");
            case DeviceTaskType.GetAvailableData:
                throw new BadParametersScenesBuilderException("Unsupported task type!");
            case DeviceTaskType.MultipleDataRequest:
                throw new BadParametersScenesBuilderException("Unsupported task type!");
            case DeviceTaskType.SetBlindOpenCloseTime:
                return DeviceControlFeature.SetBlindOpenCloseTime;
            case DeviceTaskType.SetTemperature:
                return DeviceControlFeature.SetTemperature;
            case DeviceTaskType.ChangeGatePositionPulse:
                return DeviceControlFeature.GatePulse;
            case DeviceTaskType.ChangeGatewayPositionPulse:
                return DeviceControlFeature.GatewayPulse;
            case DeviceTaskType.SetFacadePosition:
                return DeviceControlFeature.SetFacadePositionAndTilt;
            case DeviceTaskType.GetFacadeType:
                return DeviceControlFeature.SetFacadePositionAndTilt;
            case DeviceTaskType.LightRGBW:
                return DeviceControlFeature.LightRGBW;
            case DeviceTaskType.PreciseGateControl:
                return DeviceControlFeature.SetGatePositionPrecise;
            default:
                throw new BadParametersScenesBuilderException("Unsupported task type!");
        }
    }

    protected async GetSupportedTaskTypesAsync(): Promise<SupportedTaskTypes[]> {
        const version = await Api.Get<IUpdatesProvider>(UpdatesProvider.ServiceName).GetSoftwareRuntimeInfoAsync();
        const [major, minor] = version.SoftwareVersion.split(".").map(v => parseInt(v));

        const result = [SupportedTaskTypes.DeviceTask];

        if (major > 5 || (major == 5 && minor >= 27))
            result.push(SupportedTaskTypes.DelayTask);

        if (major > 5 || (major == 5 && minor >= 37))
            result.push(SupportedTaskTypes.LockExecutionTask);

        if (major > 6 || (major == 6 && minor >= 3))
            result.push(SupportedTaskTypes.NotifyTaskAppNotify);

        return result;
    }


    /**
     * Function builds SequenceRequestData object, this obj. can be send via API to controller to modify or add sequence
     * SequenceRequestData should be pass to EditSequenceAsync(sequence: SequenceRequestData) or CreateSequenceAsync(sequence: SequenceRequestData) methods in ScenesService;
     * @returns 
     */
    protected Build(): SequenceRequestData {
        const findLastRightArgumentAndRemoveTaskId = (sequence: SequenceRequestData): void => {
            //Last arg
            if (sequence.RightArgumentType == ArgumentTypeNum.Null) {
                sequence.Tasks.ControllerTasks = sequence.Tasks.ControllerTasks.map(t => {
                    if (Object.hasOwn(t, "_taskGuid"))
                        delete (t as any)["_taskGuid"];

                    return t;
                })
            }
            else
                return findLastRightArgumentAndRemoveTaskId(sequence.RightArgument as SequenceRequestData);
        }

        if (this._result != null) {
            //Removing taskGuid for internal purposes
            findLastRightArgumentAndRemoveTaskId(this._result);

            return this._result;
        }

        else throw Error("Cannot build sequence, sequence not configured!")
    }

    private FindArgumentWithGuid(sequence: SequenceRequestData, sequenceGuid: string): SequenceRequestData | null {
        if (sequence.Guid == sequenceGuid) {
            return sequence;
        }
        else if (sequence.RightArgumentType == ArgumentTypeNum.Condition)
            return this.FindArgumentWithGuid(sequence.RightArgument as SequenceRequestData, sequenceGuid);
        else return null;
    }

    /**
     * Function returns allowed arguments for current context
     */
    public GetAllowedArguments(): ArgumentParams[] {
        const result: ArgumentParams[] = [];

        //First argument, all arguments are allowed with atMeetCondition = true
        if (this._result == null) {
            result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsAstronomicalClockWithOffset, InitialAtMeetConditionValue: true, ChangableAtMeetCondition: false });
            result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsDaysOfWeek, InitialAtMeetConditionValue: true, ChangableAtMeetCondition: false });
            result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsDeviceState, InitialAtMeetConditionValue: true, ChangableAtMeetCondition: false });
            result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsSceneExecuted, InitialAtMeetConditionValue: true, ChangableAtMeetCondition: false });
            result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsTime, InitialAtMeetConditionValue: true, ChangableAtMeetCondition: false });
            result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsTimeSpan, InitialAtMeetConditionValue: true, ChangableAtMeetCondition: false });
        }
        //Second argument, constraints based on first argument
        else if (this._result != null && (this._result.RightArgument == null || this._result.RightArgument instanceof EmptySequenceRequestData)) {
            if (this._result.LeftArgumentType == ArgumentTypeNum.ArgumentAsDaysOfWeek) {
                result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsTime, InitialAtMeetConditionValue: true, ChangableAtMeetCondition: true });
                result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsAstronomicalClockWithOffset, InitialAtMeetConditionValue: true, ChangableAtMeetCondition: true });
            }

            if (this._result.LeftArgumentType == ArgumentTypeNum.ArgumentAsTime) {
                result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsDaysOfWeek, InitialAtMeetConditionValue: false, ChangableAtMeetCondition: false });
            }

            if (this._result.LeftArgumentType == ArgumentTypeNum.ArgumentAsAstronomicalClockWithOffset) {
                result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsDaysOfWeek, InitialAtMeetConditionValue: false, ChangableAtMeetCondition: false });
            }
        }

        //Add default arguments
        if (result.any(a => a.ArgumentType == SupportedArgumentTypes.ArgumentAsAstronomicalClockWithOffset) == false)
            result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsAstronomicalClockWithOffset, InitialAtMeetConditionValue: false, ChangableAtMeetCondition: true });

        if (result.any(a => a.ArgumentType == SupportedArgumentTypes.ArgumentAsDaysOfWeek) == false)
            result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsDaysOfWeek, InitialAtMeetConditionValue: false, ChangableAtMeetCondition: true });

        if (result.any(a => a.ArgumentType == SupportedArgumentTypes.ArgumentAsDeviceState) == false)
            result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsDeviceState, InitialAtMeetConditionValue: true, ChangableAtMeetCondition: true });

        if (result.any(a => a.ArgumentType == SupportedArgumentTypes.ArgumentAsSceneExecuted) == false)
            result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsSceneExecuted, InitialAtMeetConditionValue: true, ChangableAtMeetCondition: false });

        if (result.any(a => a.ArgumentType == SupportedArgumentTypes.ArgumentAsTime) == false)
            result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsTime, InitialAtMeetConditionValue: false, ChangableAtMeetCondition: true });

        if (result.any(a => a.ArgumentType == SupportedArgumentTypes.ArgumentAsTimeSpan) == false)
            result.push({ ArgumentType: SupportedArgumentTypes.ArgumentAsTimeSpan, InitialAtMeetConditionValue: true, ChangableAtMeetCondition: false });
        //End of default arguments

        return result;
    }

    /**
     * Function validate entire sequence and returns validation result
     * If sequence is invalid or needs to be optimized, we can fix it by TryFix() method or ignore validation by Ignore() method
    */
    protected ValidateScene(): SceneValidation {
        if (this._result == null)
            throw new Error("No context!");

        const errors: ValidationErrors[] = [];
        const findArgumentOfType = (sequence: SequenceRequestData, argType: ArgumentTypeNum, atArgument: boolean): SequenceRequestData | null => {
            //Last arg
            if (sequence.RightArgumentType == ArgumentTypeNum.Null) {
                if (sequence.LeftArgumentType == argType && sequence.AtMeetCondition == atArgument)
                    return sequence;
                else
                    return null;
            }

            if (sequence.LeftArgumentType == argType && sequence.AtMeetCondition == atArgument)
                return sequence as SequenceRequestData;
            else
                return findArgumentOfType(sequence.RightArgument as SequenceRequestData, argType, atArgument);
        }

        //Check if 'days of week' is 'at' and has children like time or astronomical clock argument
        //If yes, then it should be optimized (swapped places)
        const daysOfWeekArg = findArgumentOfType(this._result, ArgumentTypeNum.ArgumentAsDaysOfWeek, true);
        if (daysOfWeekArg != null) {
            if ((daysOfWeekArg.RightArgument as SequenceRequestData)?.LeftArgumentType == ArgumentTypeNum.ArgumentAsTime)
                errors.push(ValidationErrors.OptimizeDayOfWeekArgument);
            else if ((daysOfWeekArg.RightArgument as SequenceRequestData)?.LeftArgumentType == ArgumentTypeNum.ArgumentAsAstronomicalClockWithOffset)
                errors.push(ValidationErrors.OptimizeDayOfWeekArgument);
        }

        //Check if 'time' is 'at' and has children like days of week argument wchich is also 'at' condition
        //If yes, then it should be switched to 'if' condition
        const timeArg = findArgumentOfType(this._result, ArgumentTypeNum.ArgumentAsTime, true);
        if (timeArg != null) {
            if ((timeArg.RightArgument as SequenceRequestData)?.LeftArgumentType == ArgumentTypeNum.ArgumentAsDaysOfWeek && (timeArg.RightArgument as SequenceRequestData)?.AtMeetCondition)
                errors.push(ValidationErrors.OptimizeAtInDayOfWeekArgument);
        }

        return {
            IsValid: errors.length == 0,
            Errors: errors,
            TryFix: () => this.TryFix(errors),
            Ignore: () => this.Commit()
        }
    }

    private TryFix(errors: ValidationErrors[]): AddOrRemoveTasks {
        if (this._result == null)
            throw new Error("No context!");

        if (errors.includes(ValidationErrors.OptimizeDayOfWeekArgument)) {

            const findDayOfWeekArgWithTimeChild = (sequence: SequenceRequestData): SequenceRequestData | null => {
                if (sequence.LeftArgumentType == ArgumentTypeNum.ArgumentAsDaysOfWeek){
                    if ((sequence.RightArgument as SequenceRequestData)?.LeftArgumentType == ArgumentTypeNum.ArgumentAsTime || (sequence.RightArgument as SequenceRequestData)?.LeftArgumentType == ArgumentTypeNum.ArgumentAsAstronomicalClockWithOffset)
                        return sequence;
                    else
                        return findDayOfWeekArgWithTimeChild(sequence.RightArgument as SequenceRequestData);
                }
                else if (sequence.RightArgumentType == ArgumentTypeNum.Null)
                    return null;
                else
                    return findDayOfWeekArgWithTimeChild(sequence.RightArgument as SequenceRequestData);
            }

            const daysOfWeekArg = findDayOfWeekArgWithTimeChild(this._result);
            if (daysOfWeekArg == null)
                throw new Error("Something wrong! Cannot find 'days of week' argument! Cannot fix!");

            const copyDaysOfWeekArg = structuredClone(daysOfWeekArg);
            const nextArg = daysOfWeekArg.RightArgument as SequenceRequestData;

            //swap arguments, and change dayofweek argument to 'if' condition
            daysOfWeekArg.LeftArgument = nextArg.LeftArgument;
            daysOfWeekArg.LeftArgumentType = nextArg.LeftArgumentType;
            daysOfWeekArg.HandledType = nextArg.HandledType;
            nextArg.LeftArgument = copyDaysOfWeekArg.LeftArgument;
            nextArg.LeftArgumentType = copyDaysOfWeekArg.LeftArgumentType;
            nextArg.HandledType = copyDaysOfWeekArg.HandledType;
            nextArg.AtMeetCondition = false;
        }

        const remainingErrors = this.ValidateScene().Errors;

        if (remainingErrors.includes(ValidationErrors.OptimizeAtInDayOfWeekArgument)) {
            const findTimeArgWithDayOfWeekChild = (sequence: SequenceRequestData): SequenceRequestData | null => {
                if (sequence.LeftArgumentType == ArgumentTypeNum.ArgumentAsTime)
                {
                    if ((sequence.RightArgument as SequenceRequestData)?.LeftArgumentType == ArgumentTypeNum.ArgumentAsDaysOfWeek && (sequence.RightArgument as SequenceRequestData)?.AtMeetCondition)
                        return sequence;
                    else
                        return findTimeArgWithDayOfWeekChild(sequence.RightArgument as SequenceRequestData);
                }
                else if (sequence.RightArgumentType == ArgumentTypeNum.Null)
                    return null;
                else
                    return findTimeArgWithDayOfWeekChild(sequence.RightArgument as SequenceRequestData);
            }

            const timeArg = findTimeArgWithDayOfWeekChild(this._result);
            if (timeArg == null)
                return this.Commit();

            (timeArg.RightArgument as SequenceRequestData).AtMeetCondition = false;
        }

        return this.Commit();
    }
}

export type EditSequence = {
    GetNext: () => ISequence | null;
    GetPrevious: () => ISequence | null;
    GetByGuid: (sequenceGuid: string) => ISequence | null;
    GetAllowedArguments: () => ArgumentParams[];
    RemoveSequence: (sequenceGuid: string) => Commit;
    ArgumentAsAstronomicalClockWithOffset: (arg: AstronomicalClockWithOffsetArgument, atMeetCondition: boolean, comparasion?: ConditionsTypes) => AddOrEditArgument;
    ArgumentAsDaysOfWeek: (arg: DayOfWeekArgument[], atMeetCondition: boolean, comparison?: ConditionsTypes) => AddOrEditArgument;
    ArgumentAsTimeSpan: (arg: SimpleTimeSpanArgument, comparison?: ConditionsTypes) => AddOrEditArgument;
    ArgumentAsTime: (arg: TimeArgument, atMeetCondition: boolean, comparison?: ConditionsTypes) => AddOrEditArgument;
    ArgumentAsDeviceState: (arg: DeviceStateArgument, atMeetCondition: boolean, atMeetConditionTimeout: number, comparison?: ConditionsTypes) => AddOrEditArgument;
    ArgumentAsSceneExecuted: (arg: ISequenceInfo) => AddOrEditArgument;
    RemoveTask: (taskGuid: string) => void;
}

export type AddOrEditArgument = {
    AddArgument: () => Commit;
    EditArgument: (sequenceArgumentGuid: string) => Commit;
}

export type Commit = {
    SequenceData: ISequence | null;
    GetNext: () => ISequence | null;
    GetPrevious: () => ISequence | null;
    GetByGuid: (sequenceGuid: string) => ISequence | null;
    GetAllowedArguments: () => ArgumentParams[];
    RemoveSequenceByGuid: (sequenceGuid: string) => Commit;
    Commit: () => AddOrRemoveTasks;
    ArgumentAsActionWithEmptyCondition: () => AddOrEditArgument;
    ArgumentAsAstronomicalClockWithOffset: (arg: AstronomicalClockWithOffsetArgument, atMeetCondition: boolean, comparasion?: ConditionsTypes) => AddOrEditArgument;
    ArgumentAsDaysOfWeek: (arg: DayOfWeekArgument[], atMeetCondition: boolean, comparison?: ConditionsTypes) => AddOrEditArgument;
    ArgumentAsTimeSpan: (arg: SimpleTimeSpanArgument, comparison?: ConditionsTypes) => AddOrEditArgument;
    ArgumentAsTime: (arg: TimeArgument, atMeetCondition: boolean, comparison?: ConditionsTypes) => AddOrEditArgument;
    ArgumentAsDeviceState: (arg: DeviceStateArgument, atMeetCondition: boolean, atMeetConditionTimeout: number, comparison?: ConditionsTypes) => AddOrEditArgument;
    ArgumentAsSceneExecuted: (arg: ISequenceInfo) => AddOrEditArgument;
}

export type AddOrRemoveTasks = {
    GetSupportedTypesOfTasks: () => Promise<SupportedTaskTypes[]>;
    DeviceTask: (task: IDeviceTask) => AddOrEditTask;
    DelayTask: (delay: DelayTask) => AddOrEditTask;
    NotifyTask: (notification: NotifyTask) => AddOrEditTask;
    LockExecutionTask: (lockTask: LockExecutionTask) => AddOrEditTask;
    RemoveTask: (taskId: string) => void;
    ValidateScene: () => SceneValidation;
    Build: () => SequenceRequestData;
    BuildToISequence: () => ISequence;
};
export type SceneValidation = {
    IsValid: boolean;
    Errors: ValidationErrors[];
    TryFix: () => AddOrRemoveTasks;
    Ignore: () => AddOrRemoveTasks;
}

export enum ValidationErrors {
    OptimizeDayOfWeekArgument = "OptimizeDayOfWeekArgument",
    OptimizeAtInDayOfWeekArgument = "OptimizeAtInDayOfWeekArgument"
}

export class ArgumentParams {
    ArgumentType: SupportedArgumentTypes = SupportedArgumentTypes.Unknown;
    InitialAtMeetConditionValue: boolean = false;
    ChangableAtMeetCondition: boolean = false;
}

export enum SupportedArgumentTypes {
    Unknown = "Unknown",
    ArgumentAsAstronomicalClockWithOffset = "ArgumentAsAstronomicalClockWithOffset",
    ArgumentAsDaysOfWeek = "ArgumentAsDaysOfWeek",
    ArgumentAsTimeSpan = "ArgumentAsTimeSpan",
    ArgumentAsTime = "ArgumentAsTime",
    ArgumentAsDeviceState = "ArgumentAsDeviceState",
    ArgumentAsSceneExecuted = "ArgumentAsSceneExecuted",
}

export type AddOrEditTask = {
    AddTask: () => AddOrRemoveTasks;
    EditTask: (taskGuid: string) => AddOrRemoveTasks;
    TaskId: string;
}

export class SequenceRequestData {
    Tasks: TasksRequestData = new TasksRequestData();
    Guid: string = "";
    ObjectType: number = 1;
    Name: string = "";
    Icon: string = "";
    AtMeetCondition: boolean = false;
    ExecuteOnce: boolean = false;
    IsDisabled: boolean = false;
    HandledType: HandledType = HandledType.Unknown
    ConditionType: ConditionsTypes = ConditionsTypes.Unknown;
    ConditionTimeout: number = 0;
    LeftArgumentType: ArgumentTypeNum = ArgumentTypeNum.Null;
    RightArgumentType: ArgumentTypeNum = ArgumentTypeNum.Null;
    LeftArgument: LeftArgumentRequestData = new LeftArgumentRequestData({}, ArgumentTypeNum.Null);
    RightArgument: SequenceRequestData | EmptySequenceRequestData = new EmptySequenceRequestData();
}

class EmptySequenceRequestData {
    HandledType: number = 0;
    Argument: null = null;
}

class LeftArgumentRequestData {
    constructor(argument: any, handledType: ArgumentTypeNum) {
        this.Argument = argument;
        this.HandledType = handledType;
    }
    Argument: any;
    HandledType: ArgumentTypeNum;
}

class LeftArgumentDeviceStateRequestData {
    DeviceStateType: DeviceStateType = DeviceStateType.Unknown;
    Channel: number = 0;
    DeviceGuid: string = "";
    ComparisonMethod: DeviceStateComparisonMethod = DeviceStateComparisonMethod.Default;
    ComparisonParams: any = null;
    CheckedDeviceState: any = {};
}

class TasksRequestData {
    ControllerTasks: ControllerTaskRequestData[] = [];
    DevicesTasks: DeviceTaskRequestData[] = [];
}

class ControllerTaskRequestData {
    Guid: string = Guid.NewGuid();
    RunParallel: boolean = false;
    TaskType: SceneTaskTypeNum = SceneTaskTypeNum.Unknown;
}

class DeviceTaskRequestData extends ControllerTaskRequestData {
    TaskType: SceneTaskTypeNum = SceneTaskTypeNum.DeviceTask;
    DeviceGuid: string = "";
    Channel: number = 0;
    ControlFeature: DeviceControlFeature = DeviceControlFeature.TurnOn;
    Data: any;
}

class DelayTaskRequestData extends ControllerTaskRequestData {
    TaskType: SceneTaskTypeNum = SceneTaskTypeNum.DelayTask;
    Delay: string = "00:00:00"
}

class LockTaskTaskRequestData extends ControllerTaskRequestData {
    TaskType: SceneTaskTypeNum = SceneTaskTypeNum.LockExecution;
    LockType: LockType = LockType.Timeout;
    LockStartHour: string = "00:00:00"
    LockEndHour: string = "00:00:00"
    ResetLockScenesGuids: string[] = [];
}

class NotifyTaskRequestData extends ControllerTaskRequestData {
    TaskType: SceneTaskTypeNum = SceneTaskTypeNum.NotifyTask;
    Title: string = "";
    Message: string = "";
    NotifyType: NotifyType = NotifyType.None;
    NotificationClients: string[] = [];
}

enum ArgumentTypeNum {
    Null = 0,
    Condition = 1,
    ConditionGroup = 2,
    ArgumentAsInt = 3,
    ArgumentAsTimeSpan = 4,
    ArgumentAsBoolean = 5,
    ArgumentAsTimeout = 6,
    ArgumentAsDayOfWeek = 7,
    ArgumentAsDaysOfWeek = 8,
    ArgumentAsAstronomicalClock = 9,
    ArgumentAsTime = 10,
    ArgumentAsDeviceState = 11,
    ArgumentAsNumber = 12,
    ArgumentAsAstronomicalClockWithOffset = 13,
    ArgumentAsConditionInfo = 14,
}

//Errors
export class BadParametersScenesBuilderException extends Error {
    constructor(public message: string) {
        super(message);
        this.name = "BadParametersScenesBuilderException";
    }
}

export class SequenceNotFoundScenesBuilderException extends Error {
    constructor(public message: string) {
        super(message);
        this.name = "SequenceNotFoundScenesBuilderException";
    }
}

export class TaskNotFoundScenesBuilderException extends Error {
    constructor(public message: string) {
        super(message);
        this.name = "TaskNotFoundScenesBuilderException";
    }
}

export class UnsupportedArgumentScenesBuilderException extends Error {
    constructor(public message: string) {
        super(message);
        this.name = "UnsupportedArgumentScenesBuilderException";
    }
}